/**
 * @file MoveToBlockMenu.tsx
 * @description «Перенести в другой блок ›» (phase19.16 Р2а; uiux-design-r2a
 * §3, §7 — нужно для приёмки: перетаскивание работает только внутри
 * секции): список других блоков страницы с колонками — «Блок «Товар» ·
 * колонка 2». Нажатие переносит элемент в конец колонки
 * (`POST /admin/site/blocks/:id/elements/move`). На телефоне — окно на весь
 * экран, строки 48. Полная колонка — неактивна с причиной (SB64).
 */

import { SITE_LIMITS } from "@chaiberi/shared";
import { CornerDownRight } from "lucide-react";

import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";

/** Блок-получатель: название и сколько элементов в каждой колонке. */
export interface MoveTarget {
  id: string;
  title: string;
  columns: number[];
}

interface Props {
  open: boolean;
  /** Название элемента: «Цена и покупка». */
  elementLabel: string;
  targets: readonly MoveTarget[];
  busy: boolean;
  onPick: (blockId: string, column: number) => void;
  onClose: () => void;
}

export function MoveToBlockMenu({ open, elementLabel, targets, busy, onPick, onClose }: Props) {
  return (
    <Dialog open={open} onOpenChange={(next) => (!next ? onClose() : undefined)}>
      <DialogContent fullScreen className="gap-3 sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Перенести «{elementLabel}»</DialogTitle>
          <DialogDescription>Элемент встанет в конец выбранной колонки. Блок с правками сначала сохранится.</DialogDescription>
        </DialogHeader>
        {targets.length === 0 ? <p className="type-body text-on-surface-variant">На странице нет другого блока — сначала добавьте блок.</p> : null}
        <ul className="flex min-h-0 flex-col gap-1 overflow-y-auto">
          {targets.flatMap((target) =>
            target.columns.map((count, col) => {
              const full = count >= SITE_LIMITS.elementsPerColumn;
              return (
                <li key={`${target.id}:${col}`}>
                  <button
                    type="button"
                    disabled={busy || full}
                    onClick={() => onPick(target.id, col)}
                    className="flex min-h-12 w-full items-center gap-2 rounded-lg px-3 py-2 text-left outline-none hover:bg-surface-low focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    <CornerDownRight aria-hidden className="size-4 shrink-0 text-on-surface-variant" />
                    <span className="flex min-w-0 flex-1 flex-col">
                      <span className="type-body break-words text-on-surface">
                        Блок «{target.title}»{target.columns.length > 1 ? ` · колонка ${col + 1}` : ""}
                      </span>
                      {full ? <span className="type-caption text-on-surface-variant">Колонка заполнена — {SITE_LIMITS.elementsPerColumn} элементов</span> : null}
                    </span>
                  </button>
                </li>
              );
            }),
          )}
        </ul>
        <Button variant="outline" className="max-sm:h-12" onClick={onClose}>
          Отмена
        </Button>
      </DialogContent>
    </Dialog>
  );
}
